import { load, store } from "../localStorage"
import { logEventBySelector, logEvents } from "../logger"
const { Primer } = window

const PRIMER_LOCALES = [
  "ar",
  "de",
  "el",
  "en",
  "es",
  "fr",
  "pl",
  "pt",
  "bg",
  "hu",
  "ro",
  "ru",
  "sk",
  "nl",
]

export default (getView) => () => ({
  ...getView(),

  // Displays error message with reload button that refresh the page
  displayErrorMessage() {
    const offlineBlock = window.document.getElementById("offline-msg")
    offlineBlock.style.display = "block"
  },

  // Checks for the compability with locale
  isPrimerLocale(locale) {
    return PRIMER_LOCALES.includes(locale || "en")
  },

  // Primer Checkout options - config, styles, buttons, texts etc.
  getPrimerCheckoutOptions(primerSession) {
    const paymentHost = `${pipelineConfig.productApiUrl}`
    const thisStore = this.store
    let lastSelectedPaymentMethod = null

    const options = {
      container: "#primerCheckoutContainer",
      locale: pipelineConfig.language || "en",
      vault: {
        visible: false,
      },
      successScreen: false,
      errorMessage: false,
      async onCheckoutComplete({ payment }) {
        await window.analytics.track(window.getEventName("Primer Responded Success"))
        thisStore.completePayment()
        window.location = `${paymentHost}/pay/primer/post-payment/${primerSession.invoice.id}`
      },
      async onCheckoutFail(error, { payment }) {
        await thisStore.markPaymentAsFailed()
        await window.analytics.track(window.getEventName("Primer Responded Fail"))

        window.location = `${paymentHost}/pay/primer/post-payment/${primerSession.invoice.id}`
      },
      onPaymentMethodAction: (action, data) => {
        if (
          action === "PAYMENT_METHOD_SELECTED" &&
          lastSelectedPaymentMethod !== data.paymentMethodType
        ) {
          lastSelectedPaymentMethod = data.paymentMethodType
          const eventKey = ("payment-method-selected-" + data.paymentMethodType).toLowerCase()
          window.analytics.track(window.segmentEventKeys[eventKey] ?? eventKey)
        }
      },
      submitButton: {
        useBuiltInButton: this.isPrimerLocale(pipelineConfig.language),
        onVisible: (isVisible) => {
          const submitButton = window.document.getElementById("submit-button")
          if (!submitButton) return

          if (isVisible) {
            submitButton.style.display = "flex"
          } else {
            submitButton.style.display = "none"
          }
        },
        onDisable: (isDisabled) => {
          const submitButton = window.document.getElementById("submit-button")
          if (!submitButton) return

          if (isDisabled) {
            submitButton.disabled = true
          } else {
            submitButton.disabled = false
          }
        },
        onLoading: (isLoading) => {
          const submitButton = window.document.getElementById("submit-button")
          if (!submitButton) return

          if (isLoading) {
            submitButton.setAttribute("disabled", "")
            submitButton.getElementsByClassName("button-loader")[0].style.display = "block"
          } else {
            submitButton.getElementsByClassName("button-loader")[0].style.display = "none"
            submitButton.removeAttribute("disabled")
          }
        },
      },
      card: {
        cardholderName: {
          placeholder: "",
        },
      },
      paypal: {
        buttonColor: "gold",
        buttonShape: "rect",
        paymentFlow: "PREFER_VAULT",
      },
      googlePay: {
        captureBillingAddress: true,
      },
      applePay: {
        captureBillingAddress: true,
      },
      style: {
        formSpacings: {
          betweenLabelAndInput: "12px",
          betweenInputs: "16px",
        },
        inputLabel: {
          fontFamily: "Acumin, Arial, sans-serif",
          fontSize: "12px",
          lineHeight: "normal",
          fontWeight: "normal",
          color: "#404040",
        },
        input: {
          base: {
            background: "#fff",
            color: "#35383C",
          },
        },
        inputErrorText: {
          fontSize: "14px",
          fontWeight: "400",
        },
        submitButton: {
          base: {
            fontFamily: "AcuminSemi, Arial, sans-serif",
            color: "#ffffff",
            background: "linear-gradient(260deg, #54ccc2, #2a9d8f)",
            fontSize: "20px",
            fontWeight: "600",
            borderRadius: "6px",
            boxShadow: "6px",
          },
        },
      },
      redirect: {
        returnUrl: `${paymentHost}/pay/primer/post-payment/${primerSession.invoice.id}`,
        forceRedirect: true,
      },
    }

    return options
  },

  applyCustomSubmitButton(checkout) {
    // Hide primer original button
    const primerSubmitButton = window.document.getElementById(
      "primer-checkout-submit-button-container"
    )
    primerSubmitButton.style.display = "none"

    const primerWindow = window.document.getElementById("primer-checkout")
    const buttonElement = window.document.createElement("button")
    const laoderElement = window.document.createElement("div")
    const spanElement = window.document.createElement("span")

    // Apply button params
    buttonElement.id = "submit-button"
    buttonElement.className = "submit-button"
    buttonElement.name = "submit"
    buttonElement.type = "button"
    buttonElement.addEventListener(
      "click",
      () => {
        this.handleSubmitEvent()
        checkout.submit()
      },
      false
    )
    // Apply loader params
    laoderElement.className = "button-loader"
    // Apply span params
    spanElement.className = "button-text"
    spanElement.innerHTML = primerTranslations.button

    buttonElement.appendChild(spanElement)
    buttonElement.appendChild(laoderElement)
    primerWindow.appendChild(buttonElement)
  },

  // Patch the primer translations with ours
  patchTranslations(checkout) {
    const applyCustomLabel = (field) => {
      // Remove default primer label
      const currentField = window.document.getElementById(field.id)
      currentField.getElementsByClassName("PrimerCheckout__label")[0].style.display = "none"

      // Create ours
      const newField = window.document.createElement("div")
      newField.dataset.testid = "CreditCardForm.inputLabel"
      newField.classList.add("primer-input-label")
      newField.classList.add("PrimerCheckout__label")
      newField.innerHTML = field.translation

      // Display our label
      currentField.insertBefore(newField, currentField.firstChild)
    }

    const applyCustomErrorMessage = (field) => {
      const currentField = document.getElementById(field.id)
      const targetNode = currentField.getElementsByTagName("div")[0]

      // Mutation config
      const config = { childList: true }

      // Callback function executes on dom changes
      const callback = (mutationList) => {
        for (const mutation of mutationList) {
          if (mutation.type === "childList") {
            // Apply our custom translation
            if (targetNode.innerHTML !== field.translation && targetNode.innerHTML !== "") {
              targetNode.innerHTML = field.translation
            }
          }
        }
      }
      // Create/Observe the node mutations
      const observer = new MutationObserver(callback)
      observer.observe(targetNode, config)
    }

    const primerFields = [
      {
        labelField: {
          id: "primer-checkout-card-number-field",
          translation: primerTranslations.cardnumber_label,
        },
        errorField: {
          id: "primer-checkout-card-number",
          translation: primerTranslations.error.empty_field,
        },
      },
      {
        labelField: {
          id: "primer-checkout-card-expirydate-field",
          translation: primerTranslations.expire_label,
        },
        errorField: {
          id: "primer-checkout-card-expirydate-error",
          translation: primerTranslations.error.empty_field,
        },
      },
      {
        labelField: {
          id: "primer-checkout-card-cvv-field",
          translation: primerTranslations.cvc_label,
        },
        errorField: {
          id: "primer-checkout-card-cvv-error",
          translation: primerTranslations.error.empty_field,
        },
      },
      {
        labelField: {
          id: "primer-checkout-cardholder-name-field",
          translation: primerTranslations.username_label,
        },
        errorField: {
          id: "primer-checkout-cardholder-name-error",
          translation: primerTranslations.error.empty_field,
        },
      },
    ]

    primerFields.forEach((field) => {
      applyCustomLabel(field.labelField)
      applyCustomErrorMessage(field.errorField)
    })

    this.applyCustomSubmitButton(checkout)
  },

  // Creates Primer session from our BE api-diet
  async createSession(offerId, pipelineData) {
    if (pipelineData?.preloaded && pipelineData?.checkout) {
      return pipelineData?.checkout
    }

    try {
      return await this.store.createPrimerSession(offerId)
    } catch (e) {
      console.error(e)
      this.displayErrorMessage()
      return
    }
  },

  // Renders a Primer Checkout
  async renderCheckout(primerSession) {
    try {
      const spinner = document.getElementById("primer-loading")
      spinner.classList.toggle("hidden")

      const options = this.getPrimerCheckoutOptions(primerSession)
      const primerCheckout = await Primer.showUniversalCheckout(
        primerSession.invoice.session_identifier,
        options
      )

      window.analytics.track(window.getEventName("Primer Checkout Loaded"))

      spinner.classList.toggle("hidden")

      // If primer doesn't support locale add from translations
      if (!this.isPrimerLocale(pipelineConfig.language)) {
        this.patchTranslations(primerCheckout)
      } else {
        this.applyCustomSubmitButton(primerCheckout)
      }

      return primerCheckout
    } catch (e) {
      console.log(e)
      this.displayErrorMessage()
      return
    }
  },

  async onInit() {
    this.store = Alpine.store("data")
    const pipelineData = this.store.values

    const data = load("data")
    const primer = data.values.primer
    const primerSession = await this.createSession(pipelineConfig.productOfferId, pipelineData)

    if (!primerSession) {
      console.error("The Primer session was not created!")
      this.displayErrorMessage()
      window.analytics.track(window.getEventName("Error FailedToFetch Displayed"))
      return
    }

    // Update pipeline
    this.store.initiateCheckout(primerSession.invoice.identifier, primerSession)

    logEventBySelector("#primer-checkout-scene-loading", logEvents.primerLoaderRendered, {
      orderId: data?.values?.id,
      userId: data?.values?.userId,
      invoiceId: primerSession.invoice.id,
    })

    logEventBySelector('[data-primer-frame="cardNumber-card"]', logEvents.primerCheckoutRendered, {
      orderId: data?.values?.id,
      userId: data?.values?.userId,
      invoiceId: primerSession.invoice.id,
    })

    if (new Date(primer?.expiry_date) <= new Date() || primer?.invoice?.completed_at !== null) {
      const expiryDate = new Date()
      expiryDate.setSeconds(expiryDate.getSeconds() + primerSession.invoice.session_time_left)

      data.values.primer = {
        expiryDate,
        invoice: primerSession.invoice,
        isLastChance: false,
      }

      // Save new date
      store("data", data)
    }

    if (!data.values.primer.isLastChance && data.values.exitIntent) {
      const lastChance = await this.getLastChance(data.values.primer)

      if (lastChance) {
        data.values.primer.invoice.session_identifier = lastChance.data.session_identifier
        data.values.primer.isLastChance = true

        store("data", data)
      } else {
        data.values.primer.invoice.completed_at = true
        return store("data", data)
      }
    }

    // Render checkout
    this.renderCheckout(primerSession)
  },

  async getLastChance(primer) {
    const response = await fetch(
      `${pipelineConfig.productApiUrl}/pay/primer/invoices/${primer.invoice.id}/chance`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
      }
    ).then((response) => response.json())

    if (!response.error) return response

    return false
  },
  handleSubmitEvent() {
    window.analytics.track(window.getEventName("Credit Card Submitted"))

    setTimeout(function () {
      if (
        document.querySelectorAll(".PrimerCheckout__helperText--error.PrimerCheckout--entered")
          .length > 0
      ) {
        window.analytics.track(window.getEventName("Invalid Form Submitted"))
      }
    }, 1500)
  },
})
